/**
 * Connection secrets entered in the panel (0.4.1, Ayarlar > Bağlantılar). Sercan types the
 * Telegram bot token, his Telegram user id, the Claude OAuth token, the two Genesis keys,
 * the Etsy app credentials and the shop mailbox (v0.1.5) into the panel once; the API seals
 * each value (AES-256-GCM under TWIN_PII_KEY, a subkey
 * per purpose) into vault.connection_secrets and only ever returns the last four
 * characters again. The worker reads the store for the GM and the Genesis clients, the bot
 * reads its token and owner id through GET /bot/config (bot service token, bot_api network).
 *
 *   GET    /api/v1/connections              panel: status, last4 and setAt per slot, never a value
 *   PUT    /api/v1/connections/{slot}       panel: { value } replaces the slot (login rate bucket)
 *   DELETE /api/v1/connections/{slot}       panel: clears the slot
 *   POST   /api/v1/connections/{slot}/test  panel: a server side test where one exists
 *   GET    /api/v1/bot/config               bot: the decrypted Telegram token, owner id and a version
 */
import { z } from 'zod';
import { isoDateTimeSchema } from '../ids.js';
export const CONNECTION_SLOTS = [
    'telegram_bot_token',
    'telegram_owner_id',
    'claude_oauth_token',
    'anthropic_api_key',
    'genesis_twin_prod',
    'genesis_ajan_gorevleri',
    'etsy_keystring',
    'etsy_shared_secret',
    'gmail_address',
    'gmail_app_password',
    'brave_search_key',
    'serper_api_key',
    'google_places_key',
    'gemini_api_key',
    'here_api_key',
    'abn_lookup_guid',
    'nzbn_api_key',
    'portal_mcp_url',
    'millionverifier_api_key',
    'reoon_api_key',
    'outreach_cgp_address',
    'outreach_cgp_app_password',
    'outreach_dogo_address',
    'outreach_dogo_password',
    'ga4_service_account',
    'ga4_property_id',
    'bing_webmaster_key',
    'youtube_api_key',
    'pinterest_app_secret',
    'pinterest_sandbox_token',
    'cgtrader_cookie',
];
/** The panel connections (Ayarlar) whose grant is entered as slot values. */
export const CONNECTION_PANEL_IDS = [
    'telegram',
    'claude',
    'genesis',
    'etsy',
    'gmail',
    'brave',
    'serper',
    'places',
    'gemini',
    'here',
    'abn',
    'nzbn',
    'portal',
    'verifier',
    'domain',
    'domain-dogo',
    'ga4',
    'bing-webmaster',
    'youtube',
    'pinterest',
    'cgtrader',
];
export const connectionSlotSchema = z.enum(CONNECTION_SLOTS);
export function isConnectionSlot(value) {
    return CONNECTION_SLOTS.includes(value);
}
/**
 * Slots whose value is a secret (sealed at rest, never shown again). telegram_owner_id is
 * plain config; the Gmail address is sealed too (it is personal data, and one store rule).
 */
export const SECRET_CONNECTION_SLOTS = Object.freeze([
    'telegram_bot_token',
    'claude_oauth_token',
    'anthropic_api_key',
    'genesis_twin_prod',
    'genesis_ajan_gorevleri',
    'etsy_keystring',
    'etsy_shared_secret',
    'gmail_address',
    'gmail_app_password',
    'brave_search_key',
    'serper_api_key',
    'google_places_key',
    'gemini_api_key',
    'here_api_key',
    'abn_lookup_guid',
    'nzbn_api_key',
    'portal_mcp_url',
    'millionverifier_api_key',
    'reoon_api_key',
    'outreach_cgp_address',
    'outreach_cgp_app_password',
    'outreach_dogo_address',
    'outreach_dogo_password',
    'ga4_service_account',
    'ga4_property_id',
    'bing_webmaster_key',
    'youtube_api_key',
    'pinterest_app_secret',
    'pinterest_sandbox_token',
    'cgtrader_cookie',
]);
/** Accepted shapes per slot. The API refuses anything else with 422 connection_value_invalid. */
export const CONNECTION_VALUE_PATTERNS = Object.freeze({
    telegram_bot_token: /^[0-9]{5,15}:[A-Za-z0-9_-]{30,64}$/,
    telegram_owner_id: /^[1-9][0-9]{0,15}$/,
    claude_oauth_token: /^[A-Za-z0-9._~+/=-]{20,1024}$/,
    // v0.1.174: an Anthropic Console API key (sk-ant-api03-...), the Genel Müdür's credential once auth is api_key.
    anthropic_api_key: /^sk-ant-api[0-9]{2}-[A-Za-z0-9_-]{40,300}$/,
    genesis_twin_prod: /^bmu_live_[A-Za-z0-9_-]{43}$/,
    genesis_ajan_gorevleri: /^bmu_live_[A-Za-z0-9_-]{43}$/,
    // Etsy developer dashboard: the keystring is 24 lowercase letters and digits, the shared
    // secret a short token; both belong to the app, the shop grant (OAuth) comes separately.
    etsy_keystring: /^[a-z0-9]{20,40}$/,
    etsy_shared_secret: /^[A-Za-z0-9._~-]{8,128}$/,
    // Google: the address, and a 16 letter app password (shown in four groups, spaces optional).
    gmail_address: /^[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/,
    gmail_app_password: /^(?:[a-z]{4} ?){3}[a-z]{4}$/,
    // Brave Search API key (brave.com/search/api): letters, digits, dash and underscore.
    brave_search_key: /^[A-Za-z0-9_-]{20,128}$/,
    // Serper.dev API key (serper.dev › API key): letters and digits.
    serper_api_key: /^[A-Za-z0-9]{20,128}$/,
    // Google Cloud API key (console.cloud.google.com › Credentials): "AIza" and 35 more characters.
    google_places_key: /^AIza[0-9A-Za-z_-]{35}$/,
    // Gemini API key: Google AI Studio ("AIza" and 35 more) or a Vertex AI Studio express key ("AQ." and a long
    // token, v0.1.74); the worker picks the endpoint by the prefix. Anything else is refused before it is stored.
    gemini_api_key: /^(?:AIza[0-9A-Za-z_-]{35}|AQ\.[A-Za-z0-9_-]{20,400})$/,
    // HERE platform API key (platform.here.com): letters, digits, dash and underscore.
    here_api_key: /^[A-Za-z0-9_-]{20,128}$/,
    // ABN Lookup web services GUID (abr.business.gov.au): 8-4-4-4-12 hex.
    abn_lookup_guid: /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
    // NZBN API subscription key (api.business.govt.nz): 32 hex characters.
    nzbn_api_key: /^[0-9a-fA-F]{32}$/,
    // BMU Portal MCP address for the Teslimat desk (v0.1.108): https, an /mcp/ path and the token in it.
    portal_mcp_url: /^https:\/\/[a-z0-9.-]+\/mcp\/[0-9a-fA-F]{32,128}$/,
    // MillionVerifier API key (app.millionverifier.com › API): letters and digits (v0.1.132, outreach address check).
    millionverifier_api_key: /^[A-Za-z0-9]{16,64}$/,
    // v0.1.139: Reoon (free 20 checks a day), the cheaper address check.
    reoon_api_key: /^[A-Za-z0-9_-]{16,128}$/,
    // v0.1.133: the CadGaragePro sending mailbox (Google Workspace on cadgaragepro.net) and its app password.
    outreach_cgp_address: /^[a-z0-9._%+-]{1,64}@cadgaragepro\.net$/,
    outreach_cgp_app_password: /^(?:[a-z]{4} ?){3}[a-z]{4}$/,
    // v0.1.140: the Dogo sending mailbox (Veridyen's mail server for dogocustoms.com) and the mailbox's own password.
    outreach_dogo_address: /^[a-z0-9._%+-]{1,64}@dogocustoms\.com$/,
    outreach_dogo_password: /^\S{8,128}$/,
    // v0.1.149: read-only Google Analytics 4 for the outreach link visits: the service account's JSON key, pasted
    // whole (one line is fine), and the GA4 property number (Admin › Property settings).
    ga4_service_account: /^\{(?=[\s\S]*"type"\s*:\s*"service_account")(?=[\s\S]*"private_key"\s*:)(?=[\s\S]*"client_email"\s*:)[\s\S]{200,4000}\}$/,
    ga4_property_id: /^[0-9]{6,12}$/,
    // v0.1.166: the Bing Webmaster API key (bing.com/webmasters › Settings › API Access), one per user: letters and digits.
    bing_webmaster_key: /^[A-Za-z0-9]{24,64}$/,
    // 28 Oct 2026: the YouTube Data API v3 key (Google Cloud › Credentials), read only statistics of the channel.
    youtube_api_key: /^AIza[0-9A-Za-z_-]{35}$/,
    // 28 Oct 2026: the Pinterest app's secret (developers.pinterest.com › Apps › app 1630872); the app id is public.
    pinterest_app_secret: /^[A-Za-z0-9_-]{20,128}$/,
    // v0.1.267: the sandbox access token of the same app (developers.pinterest.com › Apps › the app › sandbox token),
    // used instead of the OAuth grant while the app has Trial access; "pina_..." and the like, one line.
    pinterest_sandbox_token: /^[A-Za-z0-9._~+/=-]{20,1024}$/,
    // 28 Oct 2026: the CGTrader session cookie (the browser's Cookie header for cgtrader.com), one line with name=value.
    cgtrader_cookie: /^(?=[^\r\n]*=)[^\r\n]{20,4000}$/,
});
/** A value the panel sends: trimmed, then checked against the slot's pattern. */
export function connectionValueAccepted(slot, value) {
    return CONNECTION_VALUE_PATTERNS[slot].test(value.trim());
}
/** The panel's connection (Ayarlar) each slot belongs to. */
export const CONNECTION_SLOT_PANEL_ID = Object.freeze({
    telegram_bot_token: 'telegram',
    telegram_owner_id: 'telegram',
    claude_oauth_token: 'claude',
    anthropic_api_key: 'claude',
    genesis_twin_prod: 'genesis',
    genesis_ajan_gorevleri: 'genesis',
    etsy_keystring: 'etsy',
    etsy_shared_secret: 'etsy',
    gmail_address: 'gmail',
    gmail_app_password: 'gmail',
    brave_search_key: 'brave',
    serper_api_key: 'serper',
    google_places_key: 'places',
    gemini_api_key: 'gemini',
    here_api_key: 'here',
    abn_lookup_guid: 'abn',
    nzbn_api_key: 'nzbn',
    portal_mcp_url: 'portal',
    millionverifier_api_key: 'verifier',
    reoon_api_key: 'verifier',
    outreach_cgp_address: 'domain',
    outreach_cgp_app_password: 'domain',
    outreach_dogo_address: 'domain-dogo',
    outreach_dogo_password: 'domain-dogo',
    ga4_service_account: 'ga4',
    ga4_property_id: 'ga4',
    bing_webmaster_key: 'bing-webmaster',
    youtube_api_key: 'youtube',
    pinterest_app_secret: 'pinterest',
    pinterest_sandbox_token: 'pinterest',
    cgtrader_cookie: 'cgtrader',
});
/** Slots with a server side test (POST /connections/{slot}/test). */
export const TESTABLE_CONNECTION_SLOTS = Object.freeze([
    'telegram_bot_token',
    'claude_oauth_token',
    'anthropic_api_key',
    'genesis_twin_prod',
    'brave_search_key',
    'serper_api_key',
    'google_places_key',
    'gemini_api_key',
    'here_api_key',
    'abn_lookup_guid',
    'nzbn_api_key',
    'portal_mcp_url',
    'millionverifier_api_key',
    'reoon_api_key',
    'outreach_cgp_app_password',
    'outreach_dogo_password',
    'ga4_service_account',
    'bing_webmaster_key',
    'youtube_api_key',
    'cgtrader_cookie',
]);
/** Slots whose test runs in the worker (the api has no egress); the claude test is a format check in the api. */
export const WORKER_TESTED_CONNECTION_SLOTS = Object.freeze([
    'telegram_bot_token',
    'genesis_twin_prod',
    'brave_search_key',
    'serper_api_key',
    'google_places_key',
    'gemini_api_key',
    'here_api_key',
    'abn_lookup_guid',
    'nzbn_api_key',
    'portal_mcp_url',
    'millionverifier_api_key',
    'reoon_api_key',
    'outreach_cgp_app_password',
    'outreach_dogo_password',
    'ga4_service_account',
    'bing_webmaster_key',
    'youtube_api_key',
    'cgtrader_cookie',
]);
/** Longest value the panel may submit (v0.1.149: a Google service account's JSON key runs to about 2,400). */
export const CONNECTION_VALUE_MAX = 4096;
/** PUT /connections/{slot}. */
export const connectionValueRequestSchema = z.strictObject({
    value: z.string().min(1).max(CONNECTION_VALUE_MAX),
});
/** State of a slot: nothing set, or set (last4 and when), never the value. */
export const connectionSlotStatusSchema = z.enum(['empty', 'set']);
export const connectionTestStatusSchema = z.enum(['queued', 'running', 'ok', 'failed']);
/** The last test of a slot, without anything secret: a bot username or a spend figure at most. */
export const connectionTestSchema = z.object({
    testId: z.string().regex(/^[1-9]\d{0,17}$/),
    status: connectionTestStatusSchema,
    requestedAt: isoDateTimeSchema,
    finishedAt: isoDateTimeSchema.nullable(),
    /** Turkish, safe to show: "@bot_adi", "harcanan 12,40 $ · kalan 87,60 $", "Telegram 401". */
    detail: z.string().max(300).nullable(),
});
export const connectionSlotStateSchema = z.object({
    slot: connectionSlotSchema,
    status: connectionSlotStatusSchema,
    /** Last four characters of the stored value; null while empty. */
    last4: z.string().min(1).max(4).nullable(),
    setAt: isoDateTimeSchema.nullable(),
    /** Whether a server side test exists for the slot. */
    testable: z.boolean(),
    test: connectionTestSchema.nullable(),
});
/** GET /connections. */
export const connectionsResponseSchema = z.object({
    slots: z.array(connectionSlotStateSchema),
});
/** PUT /connections/{slot}. */
export const connectionSetResponseSchema = z.object({
    slot: connectionSlotSchema,
    last4: z.string().min(1).max(4),
    setAt: isoDateTimeSchema,
    status: z.literal('set'),
});
/** DELETE /connections/{slot}. */
export const connectionClearResponseSchema = z.object({
    slot: connectionSlotSchema,
    status: z.literal('empty'),
    /** False when the slot was already empty. */
    cleared: z.boolean(),
});
/** POST /connections/{slot}/test: the test row (queued for the worker, or finished at once). */
export const connectionTestResponseSchema = z.object({
    slot: connectionSlotSchema,
    test: connectionTestSchema,
});
/**
 * GET /bot/config (bot only). configured is false until both the token and the owner id are
 * set; version changes whenever either of them is set, replaced or cleared, so the bot
 * restarts its long polling with the new values.
 */
export const botConfigResponseSchema = z.object({
    configured: z.boolean(),
    version: z.string().regex(/^[0-9]{1,18}:[0-9]{1,18}$/),
    botToken: z.string().regex(CONNECTION_VALUE_PATTERNS.telegram_bot_token).nullable(),
    ownerId: z.number().int().positive().max(Number.MAX_SAFE_INTEGER).nullable(),
});
/** The last four characters of a value as the panel shows them ("...7XVQ"). */
export function last4Of(value) {
    const trimmed = value.trim();
    // v0.1.141: a password may end in a symbol (cPanel makes such ones); vault.connection_secrets.last4 takes
    // only [A-Za-z0-9._~+/=:-], so any other character shows as "=" (Sercan, 2 Oct 2026: the Dogo mailbox
    // password did not save).
    return [...trimmed.slice(-4)].map((char) => (/^[A-Za-z0-9._~+/=:-]$/.test(char) ? char : '=')).join('');
}
/**
 * Etsy "Bağla" (v0.1.8): OAuth 2.0 with PKCE against Etsy Open API v3. The api starts the
 * flow (GET connectPath, panel session) and takes the browser back (GET callbackPath,
 * public, bound to the state); the worker exchanges the code and keeps the tokens sealed.
 */
export const ETSY_OAUTH = Object.freeze({
    authorizeUrl: 'https://www.etsy.com/oauth/connect',
    tokenUrl: 'https://api.etsy.com/v3/public/oauth/token',
    apiBase: 'https://openapi.etsy.com/v3/application',
    /**
     * Read only: the shop, its listings and its receipts. email_r (v0.1.61) lets a receipt carry
     * the buyer's e-mail, which the reader stores only as a hash (buyer_email_hash) so an order
     * meets the conversation of the same address; without it Etsy sends the field empty. A
     * shop connected before this scope must press Bagla again. Writes come with a later release.
     */
    scopes: ['shops_r', 'listings_r', 'transactions_r', 'email_r'],
    connectPath: '/connections/etsy/connect',
    callbackPath: '/connections/etsy/callback',
});
/** Etsy's callback query: the state always, then either the code or an error. */
export const etsyCallbackQuerySchema = z.object({
    state: z.string().regex(/^[A-Za-z0-9_-]{32,64}$/),
    code: z.string().min(1).max(512).optional(),
    error: z.string().max(100).optional(),
    error_description: z.string().max(500).optional(),
});
/**
 * Pinterest "Bağla" (28 Oct 2026, app "CadGaragePro Twin", Trial access): OAuth 2.0 with the app id and its secret
 * (HTTP Basic at the token endpoint). It reads the account, its boards, its pins and their numbers, and since v0.1.265
 * writes pins and boards for the pin queue. The access token lives 30 days, the refresh token a year; the worker renews
 * it in the daily channel read.
 */
export const PINTEREST_OAUTH = Object.freeze({
    clientId: '1630872',
    authorizeUrl: 'https://www.pinterest.com/oauth/',
    tokenUrl: 'https://api.pinterest.com/v5/oauth/token',
    apiBase: 'https://api.pinterest.com/v5',
    /** v0.1.265: Trial access writes go here; pins and boards made there are seen only by their creator. */
    sandboxApiBase: 'https://api-sandbox.pinterest.com/v5',
    // v0.1.265: pins:write and boards:write for the pin queue; a grant from before lacks them and needs a new Bağla.
    scopes: ['user_accounts:read', 'boards:read', 'pins:read', 'pins:write', 'boards:write'],
    connectPath: '/connections/pinterest/connect',
    callbackPath: '/connections/pinterest/callback',
});
/** The scope a grant needs before the pin queue may publish with it (v0.1.265). */
export const PINTEREST_WRITE_SCOPE = 'pins:write';
/** What the panel says about a grant without pins:write (Kanallar and the Pinterest connection). */
export const PINTEREST_NO_WRITE_TR = 'Yeniden bağlanmalı: pin yazma izni yok';
/**
 * v0.1.267: under Trial access the pin queue writes to Pinterest's sandbox, which refuses the OAuth grant's token; it
 * takes the app's own sandbox token (slot pinterest_sandbox_token). The hold codes and what the panel says about them.
 */
export const PINTEREST_SANDBOX_MISSING = 'pinterest_sandbox_token_missing';
export const PINTEREST_SANDBOX_INVALID = 'pinterest_sandbox_token_invalid';
export const PINTEREST_SANDBOX_MISSING_TR = 'Deneme ortamı anahtarı girilmedi: Ayarlar › Bağlantılar › Pinterest › Deneme ortamı anahtarı';
export const PINTEREST_SANDBOX_INVALID_TR = 'Deneme ortamı anahtarı geçersiz';
/** True when the stored scopes of a grant let the pin queue publish. */
export function pinterestCanWrite(scopes) {
    return (scopes ?? []).includes(PINTEREST_WRITE_SCOPE);
}
/** Pinterest sends the same callback fields as Etsy (state, code or error). */
export const pinterestCallbackQuerySchema = etsyCallbackQuerySchema;
/** The CadGaragePro YouTube channel (@cadgaragepro3d) whose public numbers the daily read keeps. */
export const YOUTUBE_CHANNEL_ID = 'UCqkROp8gA09erMOc93OTHUw';
//# sourceMappingURL=connections.js.map